import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  AllPlanScheduleType,
  RoutineActivity,
  ScheduledAction,
  TaskItemForSchedule,
} from '@demind-inc/core';
import { v4 as uuidv4 } from 'uuid';
import { createParser, EventSourceMessage, ParseError } from 'eventsource-parser';

import { selectedDateAtom, suggestAllAIPlanStateAtom } from '../recoil';
import { useAuthContext } from '../context';
import { API_URL, getOAuthAccessToken } from '../api';
import { usePreferences, useTodoTasksCache, useCalendarEventsCache } from '../queries';
import {
  transformAISchedulerV5ResultToScheduleAction,
  appendTargetDatePlanRunResult,
  filterAllPlanSchedulesByTypes,
  hidePlanCacheBehindPendingJob,
  prepareStateForTargetDateRun,
  restorePlanCacheAfterPendingJob,
} from '../../helpers';
import {
  applyDailyPlanStreamEvent,
  DailyPlanStreamResult,
} from '../helpers/dailyPlanStreamEvents.helpers';
import {
  clearPersistedJob,
  clearPersistedJobIfMatches,
  getPersistedJob,
  isInFlightPlanDisconnect,
  jobIdBodyFields,
  PersistedJobRecord,
  setPersistedJob,
} from '../helpers/jobRecovery.helpers';
import {
  transformDBPrefToRoutine,
  transformRoutineForEndpoint,
  transformTaskPrefForEndpoint,
  transformTodosToTaskPref,
} from '../../components';
import { usePrepareScheduler } from './usePrepareScheduler';

const ALL_PLAN_TYPES: AllPlanScheduleType[] = ['meal', 'sleep', 'workout', 'todo', 'routine'];

export type SuggestAllAIPlanRunResult = {
  schedules: ScheduledAction[];
  summary: string | null;
  newSchedules: ScheduledAction[];
  acceptedIds: string[];
  autoAcceptedIds: string[];
  discardedIds: string[];
  lastRunTodoIds?: string[];
  lastRunRoutineIds?: string[];
  /** Set when this run hid the date cache. The finishing persist must pass it
   * through so an older queued write cannot uncover the previous plan. */
  jobId?: string;
};

/** Persisted alongside the daily-plan job_id so a resumed poll can replay
 * events through the same merge logic a live run would have used (the
 * target date + which plan types this run owns — undefined means "this run
 * replaces every suggestion for the date", matching replaceTargetDate). */
export interface DailyPlanJobContext {
  targetDate: string;
  typesToReplace?: AllPlanScheduleType[];
}

export const DAILY_PLAN_JOB_TYPE = 'daily_plan' as const;

const STREAM_CLOSED_MESSAGE = 'Stream closed';

export const getPersistedDailyPlanJob = (): PersistedJobRecord<DailyPlanJobContext> | null =>
  getPersistedJob<DailyPlanJobContext>(DAILY_PLAN_JOB_TYPE);

export const useSuggestAllAIPlan = () => {
  const { user } = useAuthContext();
  const [suggestAllAIPlanState, setSuggestAllAIPlanState] =
    useRecoilState(suggestAllAIPlanStateAtom);
  const setSelectedDate = useSetRecoilState(selectedDateAtom);
  const { findInCacheByDate } = useTodoTasksCache();
  const { findInCacheByDate: findEventsInCacheByDate } = useCalendarEventsCache();

  const { preferenceId = '' } = user;
  const { schedulerPreferences } = usePreferences({ preferenceId });
  const { routine } = usePrepareScheduler();

  const { skipAllPlanning, planTypes, autoAcceptTypes } = useMemo(() => {
    const allPlan = schedulerPreferences?.allPlan;
    const includeTypes = allPlan?.includeTypes;
    const nextSkipAllPlanning = Object.values(includeTypes ?? {}).every((v) => !v);
    const nextAutoAcceptTypes = allPlan?.autoAcceptTypes as Record<string, boolean> | undefined;

    const nextPlanTypes = ALL_PLAN_TYPES.filter((t) => includeTypes?.[t] !== false);
    return {
      skipAllPlanning: nextSkipAllPlanning,
      planTypes: nextPlanTypes,
      autoAcceptTypes: nextAutoAcceptTypes,
    };
  }, [schedulerPreferences]);

  // v8 (daily_plan_v4) requires the caller to resolve todos/routine selections up front —
  // it no longer fetches them server-side. These helpers reproduce the same "default
  // selection" logic already used by the scheduler chat's todo/routine preference flows.
  const getScheduledRoutineActivityIds = useCallback(
    (targetDate: string): Set<string> => {
      if (!user?.userId) {
        return new Set();
      }
      const cachedEvents = findEventsInCacheByDate(targetDate, user.userId) ?? [];
      return new Set(
        cachedEvents
          .filter(
            (event) =>
              ['routine', 'meal'].includes(event.autoScheduleFrom?.from ?? '') &&
              event.status !== 'cancelled'
          )
          .map((event) => event.autoScheduleFrom?.id)
          .filter((id): id is string => !!id)
      );
    },
    [findEventsInCacheByDate, user?.userId]
  );

  const resolveDefaultTodos = useCallback(
    (targetDate: string): TaskItemForSchedule[] => {
      const dayTasks = findInCacheByDate(targetDate);
      return transformTaskPrefForEndpoint(transformTodosToTaskPref(dayTasks, targetDate), targetDate);
    },
    [findInCacheByDate]
  );

  const resolveTodosFromLastRun = useCallback(
    (targetDate: string, lastRunTodoIds: string[]): TaskItemForSchedule[] => {
      if (!lastRunTodoIds.length) {
        return [];
      }
      const dayTasks = findInCacheByDate(targetDate);
      const pref = transformTodosToTaskPref(dayTasks, targetDate);
      const forcedSelection = Object.fromEntries(
        Object.entries(pref).map(([taskId, task]) => [
          taskId,
          { ...task, selected: lastRunTodoIds.includes(taskId) },
        ])
      );
      return transformTaskPrefForEndpoint(forcedSelection, targetDate);
    },
    [findInCacheByDate]
  );

  const resolveDefaultRoutine = useCallback(
    (targetDate: string): RoutineActivity[] => {
      const targetDay = dayjs(targetDate);
      const scheduledIds = getScheduledRoutineActivityIds(targetDate);
      const routinePref = transformDBPrefToRoutine(routine?.routineActivities ?? []);
      return transformRoutineForEndpoint(routinePref, targetDay).filter(
        (activity) => !scheduledIds.has(activity.routineActivityId)
      );
    },
    [getScheduledRoutineActivityIds, routine?.routineActivities]
  );

  const resolveRoutineFromLastRun = useCallback(
    (targetDate: string, lastRunRoutineIds: string[]): RoutineActivity[] => {
      if (!lastRunRoutineIds.length) {
        return [];
      }
      const scheduledIds = getScheduledRoutineActivityIds(targetDate);
      return (routine?.routineActivities ?? []).filter(
        (activity) =>
          lastRunRoutineIds.includes(activity.routineActivityId) &&
          !activity.hidden &&
          !scheduledIds.has(activity.routineActivityId)
      );
    },
    [getScheduledRoutineActivityIds, routine?.routineActivities]
  );

  const runSuggestAllAIPlanStream = useCallback(
    async ({
      userId,
      metricId,
      preferenceId: prefId,
      routineId,
      targetDate,
      userMessage,
      planTypes: streamPlanTypes,
      todos,
      routine: streamRoutine,
      abortSignal,
      onStatusText,
      onResult,
      onError,
      onStreamEnd,
      mocked = false,
      jobId,
    }: {
      userId: string;
      metricId: string;
      preferenceId: string;
      routineId: string;
      targetDate: string;
      userMessage: string;
      planTypes?: AllPlanScheduleType[];
      todos?: TaskItemForSchedule[];
      routine?: RoutineActivity[];
      abortSignal?: AbortSignal;
      onStatusText?: (text: string | null) => void;
      onResult?: (result: { schedules: ScheduledAction[]; summary: string | null }) => void;
      onError?: (error: Error) => void;
      onStreamEnd?: () => void;
      mocked?: boolean;
      /** Client-supplied idempotency key the v5 endpoint registers as a
       * recoverable job (see GET /api/v1/jobs/{job_id}). Not sent on the mock
       * stream — onboarding/demo runs aren't recoverable. */
      jobId?: string;
    }): Promise<{ schedules: ScheduledAction[]; summary: string | null }> => {
      return new Promise((resolve, reject) => {
        // Mock/onboarding stays on the backend v7 mock stream (not job-recoverable).
        // Live Plan My Day uses the backend v9 stream, which registers a
        // recoverable job. Poll GET /api/v1/jobs/{job_id} to resume it.
        const useJobStream = !mocked;
        const streamPath = useJobStream
          ? '/api/v1/scheduler/aiScheduler/v9/stream'
          : '/api/v1/scheduler/aiScheduler/v7/mock/stream';
        const url = new URL(`${API_URL}${streamPath}`);
        url.searchParams.append('userId', userId);

        const oAuthAccessToken = getOAuthAccessToken();
        const headers: HeadersInit = {
          'Content-Type': 'application/json',
          'X-Device-Type': 'web',
          'X-Time-Zone': dayjs.tz.guess(),
          'X-User-Id': userId,
        };
        if (oAuthAccessToken) {
          (headers as Record<string, string>).Authorization = `Bearer ${oAuthAccessToken}`;
        }

        let result: { schedules: ScheduledAction[]; summary: string | null } = {
          schedules: [],
          summary: null,
        };
        let streamEnded = false;
        // stream_end / close means science finished. A reader that just stops
        // (refresh, dropped connection) must not be treated as completion —
        // the server job is still running and recovery needs the persisted id.
        let terminal = false;
        const endStream = () => {
          if (streamEnded) return;
          streamEnded = true;
          onStreamEnd?.();
        };

        if (abortSignal) {
          abortSignal.addEventListener('abort', () => {
            onStatusText?.(null);
            endStream();
            reject(new Error('Stream aborted'));
          });
        }

        fetch(url.toString(), {
          method: 'POST',
          headers,
          signal: abortSignal,
          body: JSON.stringify({
            userId,
            metricId,
            preferencesId: prefId,
            routineId,
            targetDate,
            weekStart: targetDate,
            numDays: 1,
            userMessage,
            planTypes: streamPlanTypes,
            // Required by the backend even when empty — never omit these fields.
            todos: todos ?? [],
            routine: streamRoutine ?? [],
            // v9 reads `jobId`; chat reads `job_id`. Both must match the id we persist.
            ...jobIdBodyFields(useJobStream ? jobId : undefined),
          }),
        })
          .then(async (response) => {
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const reader = response.body?.getReader();
            if (!reader) throw new Error('Response body is not readable');

            const decoder = new TextDecoder();

            const parser = createParser({
              onEvent(event: EventSourceMessage) {
                if (event.event === 'stream_end' || event.event === 'close') {
                  terminal = true;
                  endStream();
                  resolve(result);
                  return;
                }
                // 'job' carries the server-confirmed job_id as the very first
                // event — purely informational here since we already sent our
                // own idempotency key; nothing to apply.
                if (event.event === 'job') {
                  return;
                }
                applyDailyPlanStreamEvent(
                  event.event,
                  event.data,
                  targetDate,
                  findInCacheByDate(targetDate),
                  {
                    onStatusText,
                    onResult: (newResult: DailyPlanStreamResult) => {
                      result = newResult;
                      onResult?.(newResult);
                    },
                  }
                );
              },
              onError(error: ParseError) {
                onError?.(error);
              },
            });

            try {
              while (true) {
                if (abortSignal?.aborted) {
                  reader.cancel();
                  onStatusText?.(null);
                  endStream();
                  reject(new Error('Stream aborted'));
                  return;
                }
                const { value, done } = await reader.read();
                if (done) {
                  endStream();
                  // The v9 stream is job-backed. The mock stream may close
                  // without a terminal event and must still resolve.
                  if (useJobStream && !terminal) {
                    reject(new Error(STREAM_CLOSED_MESSAGE));
                    return;
                  }
                  resolve(result);
                  break;
                }
                parser.feed(decoder.decode(value, { stream: true }));
              }
            } catch (error) {
              if (error instanceof Error && error.name === 'AbortError') {
                onStatusText?.(null);
                endStream();
                reject(error);
                return;
              }
              onError?.(error as Error);
              reject(error);
            }
          })
          .catch((error) => {
            if (
              error instanceof Error &&
              (error.name === 'AbortError' || error.message === 'Stream aborted')
            ) {
              onStatusText?.(null);
              endStream();
              reject(error);
              return;
            }
            onError?.(error as Error);
            reject(error);
          });
      });
    },
    [findInCacheByDate]
  );

  const buildUserMessage = (focusAreas: string[] = []) => {
    const base = 'Create a balanced daily plan preview.';
    const trimmed = focusAreas.map((area) => area.trim()).filter(Boolean);
    if (!trimmed.length) {
      return base;
    }
    return `${base} Additional preferences: ${trimmed.join(' ')}`;
  };

  const runSuggestAllAIPlan = useCallback(
    async ({
      focusAreas = [],
      signal,
      skipTypes = [],
      planTypes: planTypesOverride,
      mocked = false,
      todos,
      routine: routineOverride,
      targetDate: targetDateOverride,
      replaceTargetDate = false,
    }: {
      focusAreas?: string[];
      signal?: AbortSignal;
      skipTypes?: AllPlanScheduleType[];
      /** Explicit plan types from the picker; when set, these are used as-is. */
      planTypes?: AllPlanScheduleType[];
      mocked?: boolean;
      /** Explicit selection (e.g. from AllPlanPanel's per-item picker). Overrides auto-resolve. */
      todos?: TaskItemForSchedule[];
      routine?: RoutineActivity[];
      targetDate?: string;
      /**
       * Plan My Day: the response replaces every suggestion owned by the target
       * date. Scoped runs (daily defaults) leave this false so meal/sleep
       * reruns keep cached todo/routine/workout cards.
       */
      replaceTargetDate?: boolean;
    } = {}): Promise<SuggestAllAIPlanRunResult> => {
      setSuggestAllAIPlanState((prev) => ({
        ...prev,
        isLoading: true,
        statusLines: [],
        error: null,
      }));

      if (!user?.userId || !user.metricId || !user.preferenceId || !user.routineId) {
        setSuggestAllAIPlanState({
          statusLines: [],
          schedules: [],
          summary: null,
          isLoading: false,
          acceptedIds: [],
          autoAcceptedIds: [],
          discardedIds: [],
          error: null,
          targetDate: null,
          lastRunTodoIds: [],
          lastRunRoutineIds: [],
        });
        return {
          schedules: [],
          summary: null,
          newSchedules: [],
          acceptedIds: [],
          autoAcceptedIds: [],
          discardedIds: [],
        };
      }

      if (skipAllPlanning) {
        setSuggestAllAIPlanState({
          statusLines: [],
          schedules: [],
          summary: null,
          isLoading: false,
          acceptedIds: [],
          autoAcceptedIds: [],
          discardedIds: [],
          error: null,
          targetDate: null,
          lastRunTodoIds: [],
          lastRunRoutineIds: [],
        });
        return {
          schedules: [],
          summary: null,
          newSchedules: [],
          acceptedIds: [],
          autoAcceptedIds: [],
          discardedIds: [],
        };
      }

      const targetDate = targetDateOverride || dayjs().format('YYYY-MM-DD');
      setSelectedDate(dayjs(targetDate));
      // Prefer an explicit picker selection. Otherwise apply skipTypes against preference-enabled types.
      const activePlanTypes = planTypesOverride?.length
        ? Array.from(new Set(planTypesOverride.filter((t) => ALL_PLAN_TYPES.includes(t))))
        : skipTypes.length
          ? planTypes.filter((t) => !skipTypes.includes(t))
          : planTypes;

      // v8 never fetches todos/routines itself — always resolve caller items. An explicit
      // selection (e.g. from AllPlanPanel's per-item picker) always wins. Otherwise prefer the
      // previous run's selection (so a rerun doesn't silently drop/re-add items), then fall
      // back to the default (incomplete/not-yet-scheduled) selection for the day.
      const selectedTodos = activePlanTypes.includes('todo')
        ? todos !== undefined
          ? todos.filter((task) => !!task.selected)
          : suggestAllAIPlanState.lastRunTodoIds.length
            ? resolveTodosFromLastRun(targetDate, suggestAllAIPlanState.lastRunTodoIds)
            : resolveDefaultTodos(targetDate)
        : [];
      const selectedRoutine = activePlanTypes.includes('routine')
        ? routineOverride !== undefined
          ? // Caller already curated the list (e.g. Plan My Day picker). Treat missing
            // `selected` as included so we don't drop valid overrides.
            routineOverride.filter((activity) => (activity as { selected?: boolean }).selected !== false)
          : suggestAllAIPlanState.lastRunRoutineIds.length
            ? resolveRoutineFromLastRun(targetDate, suggestAllAIPlanState.lastRunRoutineIds)
            : resolveDefaultRoutine(targetDate)
        : [];
      const nextLastRunTodoIds = selectedTodos.map((task) => task.taskId).filter(Boolean);
      const nextLastRunRoutineIds = selectedRoutine
        .map((activity) => activity.routineActivityId)
        .filter((id): id is string => !!id);

      // Client-supplied idempotency key the v5 endpoint registers as a
      // recoverable job — persisted before the request is sent so a reload
      // mid-generation has something to resume from. Not used for mocked
      // (onboarding/demo) runs.
      const jobId = mocked ? undefined : uuidv4();

      try {
        // Undefined means "drop every suggestion on this date", so a local
        // sleep/meal card cannot survive a Plan My Day result that omitted it.
        const typesToReplace = replaceTargetDate ? undefined : activePlanTypes;
        let strippedState = prepareStateForTargetDateRun(
          suggestAllAIPlanState,
          targetDate,
          typesToReplace
        );

        setSuggestAllAIPlanState((prev) => {
          strippedState = prepareStateForTargetDateRun(prev, targetDate, typesToReplace);
          return {
            ...prev,
            ...strippedState,
            isLoading: true,
            statusLines: [],
            error: null,
            targetDate,
            lastRunTodoIds: activePlanTypes.includes('todo')
              ? nextLastRunTodoIds
              : prev.lastRunTodoIds,
            lastRunRoutineIds: activePlanTypes.includes('routine')
              ? nextLastRunRoutineIds
              : prev.lastRunRoutineIds,
          };
        });

        if (jobId) {
          setPersistedJob(DAILY_PLAN_JOB_TYPE, jobId, { targetDate, typesToReplace });
          // Drop this date's saved cards before the request leaves. A refresh
          // hydrates that cache on its own, and it still holds the previous job.
          hidePlanCacheBehindPendingJob(targetDate, jobId);
        }

        const response = await runSuggestAllAIPlanStream({
          userId: user.userId,
          metricId: user.metricId,
          preferenceId: user.preferenceId,
          routineId: user.routineId,
          targetDate,
          userMessage: buildUserMessage(focusAreas),
          planTypes: activePlanTypes,
          todos: selectedTodos,
          routine: selectedRoutine,
          abortSignal: signal,
          mocked,
          jobId,
          onStatusText: (text) => {
            if (!text) return;
            setSuggestAllAIPlanState((prev) => {
              if (prev.statusLines[prev.statusLines.length - 1] === text) return prev;
              return {
                ...prev,
                isLoading: true,
                statusLines: [...prev.statusLines, text].slice(-5),
              };
            });
          },
          onResult: (result) => {
            const merged = appendTargetDatePlanRunResult(
              strippedState,
              result.schedules,
              result.summary,
              typesToReplace
            );
            const keptIds = new Set(
              merged.schedules.map((schedule, index) =>
                String(schedule.id ?? `plan-${index}`)
              )
            );
            setSuggestAllAIPlanState((prev) => ({
              ...prev,
              isLoading: false,
              schedules: merged.schedules,
              summary: merged.summary,
              // Drop decisions for schedules replaced by this run (avoids stale discarded twins).
              acceptedIds: prev.acceptedIds.filter((id) => keptIds.has(id)),
              autoAcceptedIds: prev.autoAcceptedIds.filter((id) => keptIds.has(id)),
              discardedIds: prev.discardedIds.filter((id) => keptIds.has(id)),
            }));
          },
          onError: () => {
            setSuggestAllAIPlanState((prev) => ({
              ...prev,
              statusLines: [],
              error: 'Something went wrong generating your plan.',
            }));
          },
          onStreamEnd: () => {
            setSuggestAllAIPlanState((prev) => ({ ...prev, statusLines: [] }));
          },
        });

        const scopedNewSchedules = filterAllPlanSchedulesByTypes(
          response.schedules,
          typesToReplace
        );
        const merged = appendTargetDatePlanRunResult(
          strippedState,
          scopedNewSchedules,
          response.summary,
          typesToReplace
        );
        const keptIds = new Set(
          merged.schedules.map((schedule, index) => String(schedule.id ?? `plan-${index}`))
        );

        setSuggestAllAIPlanState((prev) => ({
          ...prev,
          isLoading: false,
          schedules: merged.schedules,
          summary: merged.summary,
          acceptedIds: prev.acceptedIds.filter((id) => keptIds.has(id)),
          autoAcceptedIds: prev.autoAcceptedIds.filter((id) => keptIds.has(id)),
          discardedIds: prev.discardedIds.filter((id) => keptIds.has(id)),
        }));
        if (jobId) {
          clearPersistedJobIfMatches(DAILY_PLAN_JOB_TYPE, jobId);
        }
        return {
          ...merged,
          newSchedules: scopedNewSchedules,
          acceptedIds: strippedState.acceptedIds.filter((id) => keptIds.has(id)),
          autoAcceptedIds: strippedState.autoAcceptedIds.filter((id) => keptIds.has(id)),
          discardedIds: strippedState.discardedIds.filter((id) => keptIds.has(id)),
          lastRunTodoIds: activePlanTypes.includes('todo')
            ? nextLastRunTodoIds
            : suggestAllAIPlanState.lastRunTodoIds,
          lastRunRoutineIds: activePlanTypes.includes('routine')
            ? nextLastRunRoutineIds
            : suggestAllAIPlanState.lastRunRoutineIds,
          jobId,
        };
      } catch (error) {
        // A refresh rejects the fetch (abort, "Failed to fetch", or a reader
        // that stops). The server job is still running, so keep its id and
        // the hidden cache. A real HTTP/parse failure restores the previous plan.
        const keepJobForRecovery = Boolean(jobId) && isInFlightPlanDisconnect(error);
        if (!keepJobForRecovery) {
          restorePlanCacheAfterPendingJob(targetDate);
          if (jobId) {
            clearPersistedJobIfMatches(DAILY_PLAN_JOB_TYPE, jobId);
          }
        }
        setSuggestAllAIPlanState((prev) => ({
          ...prev,
          isLoading: false,
          ...(keepJobForRecovery ? {} : { statusLines: [] }),
        }));
        return {
          schedules: [],
          summary: null,
          newSchedules: [],
          acceptedIds: [],
          autoAcceptedIds: [],
          discardedIds: [],
        };
      }
    },
    [
      runSuggestAllAIPlanStream,
      setSuggestAllAIPlanState,
      setSelectedDate,
      user?.metricId,
      user?.preferenceId,
      user?.routineId,
      user?.userId,
      skipAllPlanning,
      planTypes,
      resolveDefaultTodos,
      resolveDefaultRoutine,
      resolveTodosFromLastRun,
      resolveRoutineFromLastRun,
      suggestAllAIPlanState.lastRunTodoIds,
      suggestAllAIPlanState.lastRunRoutineIds,
    ]
  );

  const acceptSchedule = useCallback(
    (id: string) => {
      setSuggestAllAIPlanState((prev) => ({
        ...prev,
        acceptedIds: prev.acceptedIds.includes(id) ? prev.acceptedIds : [...prev.acceptedIds, id],
        autoAcceptedIds: prev.autoAcceptedIds.filter((a) => a !== id),
        discardedIds: prev.discardedIds.filter((d) => d !== id),
      }));
    },
    [setSuggestAllAIPlanState]
  );

  const discardSchedule = useCallback(
    (id: string) => {
      setSuggestAllAIPlanState((prev) => ({
        ...prev,
        discardedIds: prev.discardedIds.includes(id)
          ? prev.discardedIds
          : [...prev.discardedIds, id],
        acceptedIds: prev.acceptedIds.filter((a) => a !== id),
        autoAcceptedIds: prev.autoAcceptedIds.filter((a) => a !== id),
      }));
    },
    [setSuggestAllAIPlanState]
  );

  const resetAllPlanState = useCallback(() => {
    const pending = getPersistedDailyPlanJob();
    if (pending?.context.targetDate) {
      restorePlanCacheAfterPendingJob(pending.context.targetDate);
    }
    clearPersistedJob(DAILY_PLAN_JOB_TYPE);
    setSuggestAllAIPlanState({
      statusLines: [],
      schedules: [],
      summary: null,
      isLoading: false,
      acceptedIds: [],
      autoAcceptedIds: [],
      discardedIds: [],
      error: null,
      targetDate: null,
      lastRunTodoIds: [],
      lastRunRoutineIds: [],
    });
  }, [setSuggestAllAIPlanState]);

  return {
    skipAllPlanning,
    planTypes,
    autoAcceptTypes,
    isLoading: suggestAllAIPlanState.isLoading,
    statusLines: suggestAllAIPlanState.statusLines,
    suggestedSchedules: suggestAllAIPlanState.schedules,
    suggestedSummary: suggestAllAIPlanState.summary,
    acceptedIds: suggestAllAIPlanState.acceptedIds,
    autoAcceptedIds: suggestAllAIPlanState.autoAcceptedIds,
    discardedIds: suggestAllAIPlanState.discardedIds,
    error: suggestAllAIPlanState.error,
    targetDate: suggestAllAIPlanState.targetDate,
    runSuggestAllAIPlan,
    acceptSchedule,
    discardSchedule,
    resetAllPlanState,
  };
};
